import { useMemo } from 'react';

import { Popover, PopoverProps } from '@mui/material';
import { useField } from 'react-final-form';

import {
  mergeSx,
  Skeleton,
  SxProps,
  withProps,
  Box,
} from '@cast/design-system';

import { useSaveCheckExceptionsFiltersQuery } from 'hooks/queries/security-insights';

import { OptionalTag } from './OptionalTag';
import { useGetSelectChipSx } from '../_hooks/useGetSelectChipSx';

import { FailedToLoad } from '@cast/shared-messages-ui';

import { RffSelect } from '@cast/shared-rff-ui';

const InputSkeleton = withProps(Skeleton, {
  height: 32,
  'data-testid': 'input-skeleton',
});

type Props = {
  ruleId: string;
  index: number;
  sx?: SxProps;
};

export const KindSelect = ({ index, sx, ruleId }: Props) => {
  const { input } = useField(`groups[${index}].kinds`);
  const value: string[] = input.value || [];
  const {
    input: { value: selectedClusterIds },
  } = useField(`groups[${index}].clusters`);

  const { resources, isLoading, error, refetch } =
    useSaveCheckExceptionsFiltersQuery({
      ruleId,
    });

  const options: { name: string; value: string }[] = useMemo(
    () =>
      resources?.map((kind) => ({
        name: kind,
        value: kind,
      })) ?? [],
    [resources]
  );

  const chipSx = useGetSelectChipSx();

  if (isLoading) {
    return <InputSkeleton />;
  }

  return (
    <RffSelect
      testId={`kind-select-${index}`}
      name={`groups[${index}].kinds`}
      sx={mergeSx(sx, chipSx)}
      inputProps={{
        label: (
          <Box display="flex">
            Kind
            <OptionalTag />
          </Box>
        ),
        success: false, // Don't show green border
      }}
      inputText={!value.length ? 'Select' : 'Selected'}
      options={options}
      disabled={!selectedClusterIds?.length || !options.length}
      PopoverComponent={
        error
          ? (props: PopoverProps) => (
              <Popover {...props}>
                <FailedToLoad
                  title="Failed to load kinds"
                  sx={{
                    width: (props.anchorEl as HTMLElement)?.offsetWidth,
                    minWidth: 200,
                    padding: 16,
                  }}
                  refresh={refetch}
                />
              </Popover>
            )
          : undefined
      }
      optionLabel="name"
      optionValue="value"
      multiple
      checkable
    />
  );
};
